import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router, Routes } from '@angular/router';
import { catchError, Observable, of, tap, throwError } from 'rxjs';
import { ReleaseComponent } from '../pages/release/release.component';
import { ReleaseDetailsComponent } from '../pages/release/release-details/release-details.component';
import { ReleaseService } from './release.service';
import { authGuard } from './auth-guard.service';
import { FILE_SHARING_ROUTES } from '../pages/file-sharing/file-sharing.routing';
import { LoginComponent } from '../pages/login/login.component';

@Injectable({ providedIn: 'root' })
export class RouteInitService {
  private releaseService = inject(ReleaseService);
  private router = inject(Router);
  private loginModalRequired = false;

  get shouldShowLoginModal(): boolean {
    return this.loginModalRequired;
  }

  init(): Observable<any> {
    return this.releaseService.products().pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          error instanceof HttpErrorResponse &&
          [401, 403].includes(error.status)
        ) {
          this.loginModalRequired = true;
          return of([]);
        }

        return throwError(() => error);
      }),
      tap((products) => {
        const dynamicRoutes: Routes = [];
        const detailsRoute: Routes = [];

        if (products.length > 0) {
          dynamicRoutes.push(
            ...products.map((product) => ({
              path: `product/${product.id}`,
              component: ReleaseComponent,
              data: {
                title: product.name,
                product,
              },
            })),
          );

          detailsRoute.push({
            path: 'product/:productId/release/:id',
            component: ReleaseDetailsComponent,
            canActivate: [authGuard],
          });
        } else {
          dynamicRoutes.push({
            path: 'releases',
            component: ReleaseComponent,
          });
        }

        detailsRoute.push({
          path: 'releases/:id/details',
          component: ReleaseDetailsComponent,
          canActivate: [authGuard],
        });

        const defaultPath =
          products.length > 0 ? `product/${products[0]?.id}` : 'releases';

        const newConfig: Routes = [
          {
            path: '',
            redirectTo: defaultPath,
            pathMatch: 'full',
          },
          {
            path: 'login',
            component: LoginComponent,
          },
          ...dynamicRoutes,
          ...detailsRoute,
          ...FILE_SHARING_ROUTES,
          {
            path: '**',
            redirectTo: defaultPath,
          },
        ];

        this.router.resetConfig(newConfig);

        if (this.loginModalRequired) {
          this.router.navigate(['/login']);
        }
      }),
    );
  }
}
